import { type IconColorKey, type IconConfig } from '@littlebird/sdk';
import {
  EmojiPicker,
  useSkinTone,
  type EmojiPickerListCategoryHeaderProps,
  type EmojiPickerListComponents,
  type EmojiPickerListEmojiProps,
  type EmojiPickerListRowProps,
  type SkinTone,
} from 'frimousse';
import { forwardRef, type ReactNode, useEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui/layout/layout';
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from '@/components/ui/popover/popover';
import { TabButton } from '@/components/ui/tab-button';
import { cn } from '@/lib/utils';

import { EntityIcon } from './entity-icon';
import { ICON_COLORS, ICONS } from './icon-catalog';
import {
  readLastSkinTone,
  writeLastIconColor,
  writeLastSkinTone,
} from './icon-picker-preferences';

const ColorSwatch = forwardRef<
  HTMLButtonElement,
  {
    label: string;
    background: string;
    isSelected: boolean;
    onClick?: () => void;
    className?: string;
  }
>(function ColorSwatch(
  { label, background, isSelected, onClick, className },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      aria-pressed={isSelected}
      title={label}
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-full',
        'border transition-transform motion-safe:hover:scale-110 focus-ring',
        isSelected ? 'border-foreground' : 'border-border-strong',
        className
      )}
      style={{ background }}
      onClick={onClick}
    >
      {isSelected && <Icons.check className="size-3 text-[var(--gray-1)]" />}
    </button>
  );
});

const trailingButtonClasses =
  'flex size-8 shrink-0 items-center justify-center rounded-lg border border-border-strong bg-surface transition-colors hover:bg-surface-hover focus-ring';

function ColorPickerButton({
  iconColor,
  onSelectColor,
}: {
  iconColor: IconColorKey | null;
  onSelectColor: (color: IconColorKey | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = ICON_COLORS.find((color) => color.key === iconColor);
  const selectColor = (color: IconColorKey | null) => {
    writeLastIconColor(color);
    onSelectColor(color);
    setOpen(false);
  };

  return (
    <PopoverRoot open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Icon color"
          title="Icon color"
          className={trailingButtonClasses}
        >
          <span
            className="size-4 rounded-full border border-border-strong"
            style={{ background: active?.fg ?? 'var(--foreground-muted)' }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2.5" align="end" side="bottom">
        <div className="grid grid-cols-5 gap-2">
          <ColorSwatch
            label="Default"
            background="var(--foreground-muted)"
            isSelected={iconColor === null}
            onClick={() => selectColor(null)}
          />
          {ICON_COLORS.map((color) => (
            <ColorSwatch
              key={color.key}
              label={color.label}
              background={color.fg}
              isSelected={iconColor === color.key}
              onClick={() => selectColor(color.key)}
            />
          ))}
        </div>
      </PopoverContent>
    </PopoverRoot>
  );
}

function normalizeIcon(next: IconConfig): IconConfig | null {
  return next.code == null && next.color == null ? null : next;
}

function randomIconSlug(): string {
  return ICONS[Math.floor(Math.random() * ICONS.length)].slug;
}

const searchFieldClasses =
  'flex h-8 flex-1 items-center gap-2 rounded-lg border border-border-strong bg-surface px-2.5';

const searchInputClasses =
  'h-full w-full bg-transparent text-sm outline-none placeholder:text-foreground-muted';
const scrollAreaClasses =
  'scrollbar-hover min-h-0 flex-1 overflow-y-auto pl-3 pr-1';
const gridClasses = 'grid grid-cols-9 justify-items-center gap-1.5';
const gridCellClasses =
  'flex size-7 items-center justify-center rounded-md smoothing-default';
const GRID_COLUMNS = 9;
const EMOJI_DATA_URL = 'https://cdn.jsdelivr.net/npm/emojibase-data@16.0.3';
const EMOJI_LOAD_TIMEOUT_MS = 6000;

function IconGridButton({
  slug,
  color,
  isSelected,
  onSelect,
}: {
  slug: string;
  color: IconColorKey | null;
  isSelected: boolean;
  onSelect: (slug: string) => void;
}) {
  return (
    <button
      type="button"
      aria-label={slug}
      aria-pressed={isSelected}
      className={cn(
        gridCellClasses,
        'text-foreground-muted transition-colors hover:bg-surface-hover focus-ring',
        isSelected && 'bg-surface-active'
      )}
      onClick={() => onSelect(slug)}
    >
      <EntityIcon icon={{ type: 'icon', code: slug, color }} size={16} />
    </button>
  );
}

function IconsTab({
  value,
  onChange,
  onSelect,
}: {
  value: IconConfig | null;
  onChange: (next: IconConfig | null) => void;
  onSelect: (next: IconConfig) => void;
}) {
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLowerCase();

  const selectedSlug = value?.type === 'icon' ? (value.code ?? null) : null;
  const icons = normalizedQuery
    ? ICONS.filter(
        (entry) =>
          entry.slug.includes(normalizedQuery) ||
          entry.keywords.includes(normalizedQuery)
      )
    : ICONS;

  const selectIcon = (slug: string) => {
    onSelect({ type: 'icon', code: slug, color: value?.color ?? null });
  };

  return (
    <YStack className="h-full">
      <XStack className="items-center gap-2 px-3 py-2">
        <XStack className={searchFieldClasses}>
          <Icons.search className="size-3.5 shrink-0 text-foreground-muted" />
          <input
            autoFocus
            type="text"
            placeholder="Filter..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className={searchInputClasses}
          />
        </XStack>
        <button
          type="button"
          aria-label="Random icon"
          title="Random icon"
          className={trailingButtonClasses}
          onClick={() => selectIcon(randomIconSlug())}
        >
          <Icons.shuffle className="size-3.5 text-foreground-muted" />
        </button>
        <ColorPickerButton
          iconColor={value?.color ?? null}
          onSelectColor={(color) =>
            onChange(
              value?.type === 'emoji'
                ? { type: 'emoji', code: value.code, color }
                : normalizeIcon({
                    type: 'icon',
                    code: value?.code ?? null,
                    color,
                  })
            )
          }
        />
      </XStack>
      <div className={cn(scrollAreaClasses, 'py-3')}>
        {icons.length === 0 ? (
          <div className="py-6 text-center text-sm text-foreground-subtle">
            No icons found
          </div>
        ) : (
          <div className={gridClasses}>
            {icons.map((entry) => (
              <IconGridButton
                key={entry.slug}
                slug={entry.slug}
                color={value?.color ?? null}
                isSelected={selectedSlug === entry.slug}
                onSelect={selectIcon}
              />
            ))}
          </div>
        )}
      </div>
    </YStack>
  );
}

function SkinTonePicker({
  onSkinToneChange,
}: {
  onSkinToneChange: (skinTone: SkinTone) => void;
}) {
  const [skinTone, setSkinTone, variations] = useSkinTone('\u270B');
  const current =
    variations.find((variation) => variation.skinTone === skinTone)?.emoji ??
    '\u270B';

  return (
    <PopoverRoot>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Skin tone"
          title="Skin tone"
          className={trailingButtonClasses}
        >
          <span className="text-base leading-none">{current}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1.5" align="end" side="bottom">
        <XStack className="items-center gap-1">
          {variations.map((variation) => (
            <button
              key={variation.skinTone}
              type="button"
              aria-label={variation.skinTone}
              aria-pressed={variation.skinTone === skinTone}
              className={cn(
                'flex size-7 items-center justify-center rounded-md text-base',
                'transition-colors hover:bg-surface-hover focus-ring',
                variation.skinTone === skinTone && 'bg-surface-active'
              )}
              onClick={() => {
                setSkinTone(variation.skinTone);
                onSkinToneChange(variation.skinTone);
              }}
            >
              {variation.emoji}
            </button>
          ))}
        </XStack>
      </PopoverContent>
    </PopoverRoot>
  );
}

function EmojiCategoryHeader({
  category,
  ...props
}: EmojiPickerListCategoryHeaderProps) {
  return (
    <div
      className="bg-surface pb-1 pt-2 text-xs text-foreground-muted"
      {...props}
    >
      {category.label}
    </div>
  );
}

function EmojiRow({ children, style, ...props }: EmojiPickerListRowProps) {
  return (
    <div
      {...props}
      className={cn(gridClasses, 'scroll-my-1')}
      style={{ ...style, display: 'grid' }}
    >
      {children}
    </div>
  );
}

function EmojiButton({ emoji, ...props }: EmojiPickerListEmojiProps) {
  return (
    <button
      className={cn(gridCellClasses, 'text-lg data-[active]:bg-surface-hover')}
      {...props}
    >
      {emoji.emoji}
    </button>
  );
}

const emojiListComponents: EmojiPickerListComponents = {
  CategoryHeader: EmojiCategoryHeader,
  Row: EmojiRow,
  Emoji: EmojiButton,
};

function EmojiLoadingState({ onRetry }: { onRetry: () => void }) {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), EMOJI_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!timedOut) {
    return <span className="text-foreground-subtle">Loading emoji...</span>;
  }

  return (
    <YStack className="items-center gap-2">
      <span className="text-foreground-subtle">Could not load emoji</span>
      <button
        type="button"
        className="rounded-md px-2 py-1 text-foreground-muted transition-colors hover:bg-surface-hover hover:text-foreground focus-ring"
        onClick={onRetry}
      >
        Try again
      </button>
    </YStack>
  );
}

function EmojisTab({
  skinTone,
  onSelectEmoji,
  onSkinToneChange,
}: {
  skinTone: SkinTone;
  onSelectEmoji: (emoji: string) => void;
  onSkinToneChange: (skinTone: SkinTone) => void;
}) {
  const [attempt, setAttempt] = useState(0);

  return (
    <EmojiPicker.Root
      key={attempt}
      columns={GRID_COLUMNS}
      emojibaseUrl={EMOJI_DATA_URL}
      skinTone={skinTone}
      className="isolate flex h-full w-full flex-col"
      onEmojiSelect={(emoji) => onSelectEmoji(emoji.emoji)}
    >
      <XStack className="items-center gap-2 px-3 py-2">
        <XStack className={searchFieldClasses}>
          <Icons.search className="size-3.5 shrink-0 text-foreground-muted" />
          <EmojiPicker.Search
            autoFocus
            placeholder="Filter..."
            className={searchInputClasses}
          />
        </XStack>
        <SkinTonePicker onSkinToneChange={onSkinToneChange} />
      </XStack>
      <EmojiPicker.Viewport
        className={cn(scrollAreaClasses, 'relative outline-none')}
      >
        <EmojiPicker.Loading className="absolute inset-0 flex items-center justify-center text-sm">
          <EmojiLoadingState onRetry={() => setAttempt((n) => n + 1)} />
        </EmojiPicker.Loading>
        <EmojiPicker.Empty className="absolute inset-0 flex items-center justify-center text-sm text-foreground-subtle">
          No emoji found
        </EmojiPicker.Empty>
        <EmojiPicker.List
          className="select-none pb-3"
          components={emojiListComponents}
        />
      </EmojiPicker.Viewport>
    </EmojiPicker.Root>
  );
}

interface IconPickerProps {
  value: IconConfig | null;
  onChange: (next: IconConfig | null) => void;
  children: ReactNode;
  onOpenChange?: (open: boolean) => void;
}

export function IconPicker({
  value,
  onChange,
  children,
  onOpenChange,
}: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'icons' | 'emojis'>('icons');
  const [skinTone, setSkinTone] = useState(readLastSkinTone);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setSkinTone(readLastSkinTone());
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const selectAndClose = (next: IconConfig | null) => {
    onChange(next);
    handleOpenChange(false);
  };

  useEffect(() => {
    if (!open) return;

    let focusFrame = 0;
    const close = () => {
      setOpen(false);
      onOpenChange?.(false);
    };
    const closeOnOutsideScroll = (event: Event) => {
      if (
        event.target instanceof Node &&
        contentRef.current?.contains(event.target)
      ) {
        return;
      }
      close();
    };
    const closeOnIframeFocus = () => {
      cancelAnimationFrame(focusFrame);
      focusFrame = requestAnimationFrame(() => {
        if (document.activeElement instanceof HTMLIFrameElement) close();
      });
    };

    window.addEventListener('scroll', closeOnOutsideScroll, true);
    window.addEventListener('blur', closeOnIframeFocus);
    return () => {
      cancelAnimationFrame(focusFrame);
      window.removeEventListener('scroll', closeOnOutsideScroll, true);
      window.removeEventListener('blur', closeOnIframeFocus);
    };
  }, [open, onOpenChange]);

  return (
    <PopoverRoot open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        className="w-[22rem] overflow-hidden p-0"
        align="start"
        onClick={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}
      >
        <div className="border-b border-border px-3">
          <XStack className="items-center gap-4">
            <TabButton
              label="Icons"
              active={activeTab === 'icons'}
              onClick={() => setActiveTab('icons')}
            />
            <TabButton
              label="Emojis"
              active={activeTab === 'emojis'}
              onClick={() => setActiveTab('emojis')}
            />
            <button
              type="button"
              className="ml-auto rounded-md px-1.5 py-1 text-xs text-foreground-muted transition-colors hover:bg-surface-hover hover:text-foreground focus-ring"
              onClick={() => selectAndClose(null)}
            >
              Reset
            </button>
          </XStack>
        </div>
        <div className="h-[19rem]">
          {activeTab === 'icons' ? (
            <IconsTab
              value={value}
              onChange={onChange}
              onSelect={selectAndClose}
            />
          ) : (
            <EmojisTab
              skinTone={skinTone}
              onSkinToneChange={(nextSkinTone) => {
                writeLastSkinTone(nextSkinTone);
                setSkinTone(nextSkinTone);
              }}
              onSelectEmoji={(emoji) =>
                selectAndClose(
                  normalizeIcon({
                    type: 'emoji',
                    code: emoji,
                    color: value?.color ?? null,
                  })
                )
              }
            />
          )}
        </div>
      </PopoverContent>
    </PopoverRoot>
  );
}
